import { getMerchantId, ReparoApi } from '@api';
import {
    buildInventoryAgeingDefaults,
    buildInventoryViewDefaults,
    buildReplenishmentHealthDefaults,
    getOrgConfig,
    setDrrExcludeStockoutDays,
    setInventoryViewDefaults,
    setWarehouseOpsEnabled,
} from '@api/organization';
import { DEFAULT_AGEING_EDGES } from '@components/containers/inventoryAgeing';
import { NO_ROLLUP, STATIC_ROLLUP_OPTIONS } from '@constants/inventoryRollup';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useLockedModules } from '@hooks/useLockedModules';
import { CaretDown, CaretRight, Lock } from '@phosphor-icons/react';
import { PREMIUM_GROUP_LABEL, PREMIUM_TOOLTIP } from '@constants/lockedModules';
import type { LockedModuleKey } from '@diagon-alley/common';
import {
    NO_GROUPING,
    PRODUCT_GROUPINGS,
} from '@constants/replenishmentGrouping';
import { listWarehousesForPicker } from '@api/work-order';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ALL_VENDORS,
    createBomMapping,
    deleteBomMapping,
    getBom,
    getBomMaterialPicks,
    getBomProductPicks,
    updateBomQuantity,
    uploadBomCsv,
    type BomMaterialPick,
    type BomProductPick,
    type BomRow,
    type BomUploadResult,
} from '@api/bom-management';
import { listVendors, type VendorListItem } from '@api/vendors';
import BrandLetterheadTab from '@components/containers/settings/BrandLetterheadTab';
import DocumentNumberingTab from '@components/containers/settings/DocumentNumberingTab';
import EmailSettingsTab from '@components/containers/settings/EmailSettingsTab';
import LanesTab from '@components/containers/settings/LanesTab';
import UsersTab from '@components/containers/settings/UsersTab';
import DistributionEmailTab from '@components/containers/rebalance/DistributionEmailTab';
import DistributionPathsTab from '@components/containers/rebalance/DistributionPathsTab';
import ShelfLocationManager from '@components/containers/warehouseOps/ShelfLocationManager';
import B2bSettingsTab from '@components/containers/b2b/B2bSettingsTab';
import EntityMasterSection from '@components/containers/EntityMaster/EntityMasterSection';
import VendorSection from '@components/containers/EntityMaster/VendorSection';
import { bomVendorPills, copySharedBomLines } from '@components/containers/EntityMaster/bomOptions';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import FilterDropdown from '@components/ui/Dropdown';
import { DataTable } from '@components/ui/Table';
import {
    ActionIcon,
    Button,
    FileButton,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { IconCheck, IconDownload, IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatCount } from '@utils/currency';

type SettingSectionKey =
    | 'brandLetterhead'
    | 'documentNumbering'
    | 'productClassification'
    | 'entityMaster'
    | 'vendorDetails'
    | 'distributionPaths'
    | 'distributionEmail'
    | 'emailSettings'
    | 'lanes'
    | 'billOfMaterials'
    | 'shelfLocations'
    | 'warehouseOps'
    | 'b2bCustomers'
    | 'inventoryViews'
    | 'users';

type SettingsModalProps = {
    opened: boolean;
    onClose: () => void;
};

/* ---------- Helper ---------- */
const cn = (...classes: Array<string | false | null | undefined>) =>
    classes.filter(Boolean).join(' ');

/* ---------- Row types ---------- */
type VendorMaterialRow = {
    materialOrSku: string;
    leadTimeDays: number;
};

type SalesChannelRow = {
    internalSku: string;
    productName: string;
    amazon: string;
    flipkart: string;
    nykaa: string;
    ownWebsite: string;
};

type WarehouseRow = {
    warehouseName: string;
    location: string;
    minStockLevel: string;
};

/* ---------- Dummy data (ek jagah pe) ---------- */

const SUPPLY_VENDORS = [
    {
        name: 'ABC Chemicals Ltd',
        tag: 'Raw Materials',
        billingAddress: 'Billing Address: 123 Industrial Park, Mumbai 400001',
        dispatchAddress: 'Dispatch Address: 123 Industrial Park, Mumbai 400001',
        taxCode: 'GST123456789',
        rows: [
            { materialOrSku: 'Hyaluronic Acid', leadTimeDays: 7 },
            { materialOrSku: 'Vitamin E Oil', leadTimeDays: 5 },
        ],
    },
    {
        name: 'PackPro Industries',
        tag: 'Packaging',
        billingAddress: 'Billing Address: 456 Packaging Ave, Delhi 110001',
        dispatchAddress: 'Dispatch Address: 789 Warehouse Rd, Delhi 110002',
        taxCode: 'GST987654321',
        rows: [
            { materialOrSku: 'Glass Jar 50ml', leadTimeDays: 14 },
            { materialOrSku: 'Outer Box', leadTimeDays: 10 },
        ],
    },
];

const SALES_CHANNEL_ROWS: SalesChannelRow[] = [
    {
        internalSku: 'SKU-001',
        productName: 'Premium Face Cream 50ml',
        amazon: 'AFC–50ML–PREM',
        flipkart: 'FACECREAM–PREM–50',
        nykaa: 'PFC–50–001',
        ownWebsite: 'SKU–001',
    },
    {
        internalSku: 'SKU-002',
        productName: 'Moisturizing Serum 30ml',
        amazon: 'MS–30ML–MOIST',
        flipkart: 'SERUM–MOIST–30',
        nykaa: 'MMS–30–002',
        ownWebsite: 'SKU–002',
    },
    {
        internalSku: 'SKU-003',
        productName: 'Hydrating Toner 100ml',
        amazon: 'HT–100ML–HYD',
        flipkart: 'TONER–HYD–100',
        nykaa: 'HHT–100–003',
        ownWebsite: 'SKU–003',
    },
];

const WAREHOUSE_ROWS: WarehouseRow[] = [
    {
        warehouseName: 'Mumbai Warehouse',
        location: 'Andheri East, Mumbai 400069',
        minStockLevel: '5,000',
    },
    {
        warehouseName: 'Delhi Distribution Center',
        location: 'Okhla Industrial Area, Delhi 110020',
        minStockLevel: '3,500',
    },
    {
        warehouseName: 'Bangalore Fulfillment Hub',
        location: 'Whitefield, Bangalore 560066',
        minStockLevel: '4,000',
    },
    {
        warehouseName: 'Chennai Regional Warehouse',
        location: 'Ambattur, Chennai 600058',
        minStockLevel: '2,500',
    },
];

/**
 * `module` ties each section to the plan module it configures. A section whose module is in
 * the org's `lockedModules` moves under "Premium" in the left rail and opens on a notice
 * instead of its form, the same treatment the sidebar gives a locked destination.
 */
const SETTINGS_SECTIONS: {
    key: SettingSectionKey;
    label: string;
    /** The plan module this section belongs to. Absent = never locked, on any plan. */
    module?: LockedModuleKey;
    adminOnly?: boolean;
}[] = [
    // First, because it is the one thing here that appears on documents leaving the building.
    { key: 'brandLetterhead', label: 'Brand Letterhead', module: 'settings.brand_letterhead' },
    { key: 'documentNumbering', label: 'Order Numbering', module: 'settings.document_numbering' },
    { key: 'distributionPaths', label: 'Distribution Paths', module: 'settings.distribution_paths' },
    { key: 'distributionEmail', label: 'Distribution Email', module: 'settings.distribution_email' },
    // Beside Distribution Email because both answer "who gets told": that one is the plan a
    // person composes and sends, this one is what the system sends on its own.
    { key: 'emailSettings', label: 'Email Settings', module: 'settings.email_settings' },
    { key: 'productClassification', label: 'Product Classification', module: 'settings.product_classification' },
    { key: 'entityMaster', label: 'Entities', module: 'settings.entities' },
    // The key stays `vendorDetails`: nothing deep-links to it and no URL or stored
    // state carries it, so renaming it would be churn for its own sake.
    { key: 'vendorDetails', label: 'Vendor Mapping', module: 'settings.vendor_mapping' },
    // Next to Vendor Mapping because a lane belongs to a vendor and is picked by one.
    { key: 'lanes', label: 'Lanes', module: 'settings.lanes' },
    { key: 'billOfMaterials', label: 'Bill of Materials', module: 'settings.bill_of_materials' },
    { key: 'shelfLocations', label: 'Shelf Locations', module: 'settings.shelf_locations' },
    { key: 'warehouseOps', label: 'Warehouse Ops', module: 'settings.warehouse_ops' },
    // Admins only: it is where a brand switches B2B selling on, and every save is admin-only.
    { key: 'b2bCustomers', label: 'B2B Customers', module: 'settings.b2b_customers', adminOnly: true },
    { key: 'inventoryViews', label: 'Inventory Views', module: 'settings.inventory_views' },
    // Last, and admins only: who has a login here. No plan module on purpose - a brand must
    // always be able to manage its own people, whatever else its plan leaves out.
    { key: 'users', label: 'Users', adminOnly: true },
];

/** What a locked section opens on: the name, and who to talk to. Nothing else loads. */
const PremiumSectionNotice: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <Lock size={22} weight="fill" />
        </div>
        <Text fw={600} size="md" className="text-slate-900">
            {label} is a {PREMIUM_GROUP_LABEL} feature
        </Text>
        <Text size="sm" className="max-w-md text-slate-500">
            {PREMIUM_TOOLTIP}
        </Text>
    </div>
);

/* ---------- Common table classes (wrapper sab transparent) ---------- */

const tableClasses = {
    container:
        'bg-transparent border-0 shadow-none px-0 py-0 sm:px-0 sm:py-0 rounded-none',
    toolbar: 'hidden',
    footer: 'hidden',
    tableWrapper: 'p-0 bg-transparent border-0 shadow-none',
    table: 'border border-slate-200 rounded-2xl bg-white',
    thead: 'bg-slate-50 text-[11px] text-slate-500',
    bodyRow: 'hover:bg-slate-50/80 border-b border-slate-100 last:border-b-0',
    bodyCell: 'text-xs sm:text-sm',
};

/* ───────────────────── Bill of Materials Tab (DataSheet flat view) ───────────────────── */

const BOM_TYPE_FILTER_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'RM', label: 'Raw Material' },
    { value: 'PM', label: 'Packaging' },
];

/**
 * The download IS the upload template.
 *
 * One key per side and nothing else that looks like one. `product_id` and `material_id` are
 * what the importer matches on first; the two names and the type are there so a person can
 * read the file, and the importer ignores them. The sku columns the importer also accepts are
 * deliberately left out: next to the ids they read as a second thing to fill in, and for a
 * brand whose materials carry no sku the column was blank on every line anyway.
 *
 * `unit` travels so the reader knows what `quantity` is measured in. It belongs to the
 * material, not the line, and the importer refuses a row whose unit disagrees with it.
 */
const BOM_EXPORT_COLUMNS: DataSheetColumn<BomRow>[] = [
    { key: 'productId', header: 'Product ID', csvHeader: 'product_id' },
    { key: 'productName', header: 'Product', csvHeader: 'product_name' },
    /*
     * The vendor column is REQUIRED by the importer and blank on most lines, which is exactly
     * why it has to travel in the download: a brand that types the file by hand would not know
     * to add it, and a brand that re-uploads a download without it would have its
     * per-manufacturer recipes refused. Blank means "the recipe every manufacturer uses".
     */
    { key: 'vendorId', header: 'Vendor ID', csvHeader: 'vendor_id' },
    { key: 'vendorName', header: 'Vendor', csvHeader: 'vendor_name' },
    { key: 'materialId', header: 'Material ID', csvHeader: 'material_id' },
    { key: 'materialName', header: 'Material', csvHeader: 'material_name' },
    { key: 'materialType', header: 'Type', csvHeader: 'material_type' },
    { key: 'quantity', header: 'Quantity', csvHeader: 'quantity' },
    { key: 'unit', header: 'Unit', csvHeader: 'unit' },
];

/**
 * Which vendor a line is for.
 *
 * "All vendors" says what it does rather than naming a state: a line with no vendor on it
 * applies to whoever makes the product, which is nearly every line here. It reads quieter than
 * a named vendor for that reason — the named ones are the exceptions, and the exceptions are
 * what the eye should find.
 */
function VendorBadge({ row }: { row: Pick<BomRow, 'vendorId' | 'vendorName'> }) {
    const isAllVendors = !row.vendorId;
    return (
        <span
            className="inline-block max-w-full truncate rounded px-2 py-0.5"
            title={row.vendorId ?? 'Applies to whoever makes this product'}
            style={{
                backgroundColor: isAllVendors ? 'rgba(100, 116, 139, 0.08)' : 'rgba(16, 185, 129, 0.12)',
                color: isAllVendors ? 'rgb(100, 116, 139)' : 'rgb(4, 120, 87)',
                fontSize: 11,
                fontWeight: 600,
            }}
        >
            {isAllVendors ? 'All vendors' : (row.vendorName || row.vendorId)}
        </span>
    );
}

function MaterialTypeBadge({ type }: { type: 'RM' | 'PM' }) {
    const styles =
        type === 'RM'
            ? { color: 'rgb(1, 149, 255)', bg: 'rgba(1, 149, 255, 0.1)' }
            : { color: 'rgb(219, 182, 88)', bg: 'rgba(219, 182, 88, 0.1)' };
    return (
        <span
            className="inline-block rounded px-2 py-0.5"
            style={{
                backgroundColor: styles.bg,
                color: styles.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
            }}
        >
            {type}
        </span>
    );
}

const BillOfMaterialsTab: React.FC = () => {
    const [rows, setRows] = useState<BomRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploadResult, setUploadResult] = useState<BomUploadResult | null>(null);
    /*
     * WHAT WENT WRONG, ON SCREEN. Every handler below used to end in a bare
     * `console.error`, so a failed request looked exactly like nothing happening — the
     * spinner stopped, the modal stayed open, and the reason went only to a console nobody
     * had open. That is how a 422 on Add Line went unnoticed long enough for the button to
     * have never worked at all.
     */
    const [actionError, setActionError] = useState<string | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [draftValue, setDraftValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [productPicks, setProductPicks] = useState<BomProductPick[]>([]);
    const [materialPicks, setMaterialPicks] = useState<BomMaterialPick[]>([]);
    const [picksLoading, setPicksLoading] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
    const [newQuantity, setNewQuantity] = useState<number | string>('');
    const [addSaving, setAddSaving] = useState(false);
    /** Which vendor the grid is showing: '' = every line, ALL_VENDORS = the shared ones. */
    const [vendorFilter, setVendorFilter] = useState<string>('');
    /** The vendor the Add Line modal will write against. */
    const [selectedVendor, setSelectedVendor] = useState<string>(ALL_VENDORS);
    const [vendors, setVendors] = useState<VendorListItem[]>([]);
    const [copying, setCopying] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getBom();
            setRows(data);
        } catch (err) {
            console.error('Failed to fetch BOM', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openAddModal = useCallback(async () => {
        setAddModalOpen(true);
        if (productPicks.length === 0 || materialPicks.length === 0) {
            setPicksLoading(true);
            try {
                /*
                 * THE WHOLE VENDOR LIST, NOT THE ONES MAPPED TO THIS PRODUCT. Scoping it to the
                 * product's existing vendor mappings meant that entering a BOM for a vendor the
                 * brand had not mapped yet was impossible from this screen — and entering the
                 * BOM is often the FIRST thing somebody does when a new factory takes a product
                 * on. The server checks the vendor exists; which of them make what is the vendor
                 * mapping's business, not this form's.
                 */
                const [products, materials, vendorList] = await Promise.all([
                    getBomProductPicks(),
                    getBomMaterialPicks(),
                    listVendors(),
                ]);
                setProductPicks(products);
                setMaterialPicks(materials);
                setVendors(vendorList);
            } catch (err) {
                console.error('Failed to load picker lists', err);
            } finally {
                setPicksLoading(false);
            }
        }
    }, [productPicks.length, materialPicks.length]);

    const closeAddModal = () => {
        setAddModalOpen(false);
        setSelectedProductId(null);
        setSelectedMaterialId(null);
        setSelectedVendor(ALL_VENDORS);
        setNewQuantity('');
        // Closing clears the message too, or the next open reads as already broken.
        setActionError(null);
    };

    /** The server's own message where there is one, so a 422 says which field it refused. */
    // ReparoApi rejects with `{ status: 'error', error: { message } }` for a backend 4xx, and
    // with a plain Error for everything else. Reading only the top-level message dropped every
    // backend explanation on this page behind the generic fallback.
    const describeError = (err: unknown, fallback: string) => {
        const shaped = err as { message?: unknown; error?: { message?: unknown } } | null;
        const candidate = shaped?.error?.message ?? shaped?.message;
        const message = typeof candidate === 'string' ? candidate : '';
        return message && message !== 'Error' ? message : fallback;
    };

    const handleAdd = async () => {
        if (!selectedProductId || !selectedMaterialId) return;
        const quantity = Number(newQuantity);
        if (!Number.isFinite(quantity) || quantity <= 0) return;
        setAddSaving(true);
        setActionError(null);
        try {
            await createBomMapping({
                productId: selectedProductId,
                materialId: selectedMaterialId,
                quantity,
                vendorId: selectedVendor === ALL_VENDORS ? null : selectedVendor,
            });
            await fetchData();
            closeAddModal();
        } catch (err) {
            console.error('Failed to add BOM line', err);
            setActionError(describeError(err, 'Could not add the BOM line.'));
        } finally {
            setAddSaving(false);
        }
    };

    const handleDelete = async (row: BomRow) => {
        const scope = row.vendorId ? (row.vendorName || row.vendorId) : 'All vendors';
        // An all-vendors line on a product whose manufacturers each carry their own recipe is
        // not read by any of them - say so, because that is the line most often added by
        // mistake and the one people hesitate over.
        const ownRecipes = row.vendorId
            ? []
            : [...new Set(
                  rows
                      .filter((r) => r.productId === row.productId && r.vendorId)
                      .map((r) => r.vendorName || r.vendorId!),
              )];
        const note = ownRecipes.length > 0
            ? `\n\n${ownRecipes.join(', ')} ${ownRecipes.length === 1 ? 'has' : 'have'} a recipe of ${ownRecipes.length === 1 ? 'its' : 'their'} own and will not be affected.`
            : '';
        const ok = window.confirm(
            `Delete this BOM line?\n\nMaterial: ${row.materialName}\nVendor: ${scope}${note}\n\nThis cannot be undone.`,
        );
        if (!ok) return;
        try {
            await deleteBomMapping(row.productId, row.materialId, row.vendorId);
            await fetchData();
        } catch (err) {
            console.error('Failed to delete BOM mapping', err);
            setActionError(describeError(err, 'Could not delete the BOM line.'));
        }
    };

    // The recipe is part of a line's identity now: the same product and material appear once
    // per recipe, and a key without the vendor would put two rows into one edit box.
    const makeKey = (row: Pick<BomRow, 'productId' | 'materialId' | 'vendorId'>) =>
        `${row.productId}::${row.materialId}::${row.vendorId ?? ''}`;

    /** Start a vendor's BOM as a copy of the all-vendors lines; see `copySharedBomLines`. */
    const handleCopyShared = async () => {
        if (!selectedProductId || selectedVendor === ALL_VENDORS) return;
        setCopying(true);
        setActionError(null);
        try {
            const copied = await copySharedBomLines(rows, selectedProductId, selectedVendor);
            if (copied > 0) {
                await fetchData();
                closeAddModal();
            }
        } catch (err) {
            console.error('Failed to copy the all-vendors lines', err);
            setActionError(describeError(err, 'Could not copy the all-vendors lines.'));
        } finally {
            setCopying(false);
        }
    };

    const handleUpload = async (file: File | null) => {
        if (!file) return;
        setUploading(true);
        try {
            const result = await uploadBomCsv(file);
            setUploadResult(result);
            await fetchData();
        } catch (err) {
            console.error('Failed to upload BOM', err);
            setActionError(describeError(err, 'Could not upload the BOM file.'));
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (row: BomRow) => {
        const quantity = Number(draftValue);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            setEditingKey(null);
            setDraftValue('');
            return;
        }
        setSaving(true);
        try {
            await updateBomQuantity(row.productId, row.materialId, quantity, row.vendorId);
            await fetchData();
        } catch (err) {
            console.error('Failed to update BOM quantity', err);
            setActionError(describeError(err, 'Could not save the quantity.'));
        } finally {
            setSaving(false);
            setEditingKey(null);
            setDraftValue('');
        }
    };

    const handleCancel = () => {
        setEditingKey(null);
        setDraftValue('');
    };

    /**
     * The vendors that actually have lines, in the order the pills show them.
     *
     * Derived from the rows rather than from the vendor list: a pill for a vendor with no lines
     * of its own would filter to an empty grid, and the question the pills answer is "who has a
     * BOM of their own here", not "who could have one".
     */
    const vendorPills = React.useMemo(() => bomVendorPills(rows), [rows]);

    const filteredRows = React.useMemo(() => {
        let result = rows;
        if (vendorFilter === ALL_VENDORS) {
            result = result.filter((r) => !r.vendorId);
        } else if (vendorFilter) {
            result = result.filter((r) => r.vendorId === vendorFilter);
        }
        if (typeFilter) {
            result = result.filter((r) => r.materialType === typeFilter);
        }
        if (search) {
            const s = search.toLowerCase();
            result = result.filter(
                (r) =>
                    (r.productName ?? '').toLowerCase().includes(s) ||
                    (r.productSku ?? '').toLowerCase().includes(s) ||
                    // The id is what the Product cell prints under the name, and it need not be
                    // the sku (The Stack's DR30 sells as METAMAG30).
                    r.productId.toLowerCase().includes(s) ||
                    r.materialName.toLowerCase().includes(s) ||
                    (r.materialSku ?? '').toLowerCase().includes(s) ||
                    r.materialId.toLowerCase().includes(s) ||
                    (r.vendorName ?? '').toLowerCase().includes(s),
            );
        }
        return result;
    }, [rows, typeFilter, search, vendorFilter]);

    // The whole BOM, never the filtered view: a template with half the lines missing would
    // read as "these are all the lines", and a re-upload of it cannot delete the rest anyway.
    const fetchAllForDownload = useCallback(async () => ({ rows }), [rows]);

    const selectedMaterialUnit = materialPicks.find(
        (m) => m.materialId === selectedMaterialId,
    )?.unit;

    /** Every vendor the brand has, plus the "applies to all of them" option that leads. */
    const vendorOptions = React.useMemo(
        () => [
            { value: ALL_VENDORS, label: 'All vendors' },
            ...vendors
                .map((vendor) => ({
                    value: vendor.vendorId,
                    label: vendor.name || vendor.vendorId,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [vendors],
    );

    const sharedLineCount = React.useMemo(
        () =>
            selectedProductId
                ? rows.filter((r) => r.productId === selectedProductId && !r.vendorId).length
                : 0,
        [rows, selectedProductId],
    );

    // Offered only where it helps: a vendor that already has lines would be shown a button
    // that overwrites them, which is not what "copy" should ever mean.
    const canCopyShared =
        selectedVendor !== ALL_VENDORS &&
        sharedLineCount > 0 &&
        !rows.some(
            (r) => r.productId === selectedProductId && r.vendorId === selectedVendor,
        );

    const columns: DataSheetColumn<BomRow>[] = React.useMemo(
        () => [
            {
                key: 'productName',
                header: 'Product',
                width: 240,
                sticky: true,
                render: (_: unknown, r: BomRow) => (
                    // gap-1 matches the vendor portal's own BOM grid: the code under the name
                    // is a second fact about the row, and flush against it the two read as one
                    // wrapped line.
                    <div className="flex flex-col gap-1">
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'rgb(26, 32, 44)' }}>
                            {r.productName || r.productId}
                        </span>
                        {/* The id, not the sku: it is the key the downloaded file carries, so
                            the screen and the file name a product the same way. Hidden when the
                            brand's product name IS its id, which would print it twice. */}
                        {r.productName !== r.productId && (
                            <span style={{ fontSize: 11, color: 'rgb(100, 116, 139)' }}>
                                {r.productId}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                /*
                 * ALWAYS SHOWN, even for a brand whose every line applies to all vendors.
                 * Hidden until a vendor-specific line appeared, the column would be an
                 * invisible dimension: a quantity on screen would silently be one of several,
                 * and the first person to add a vendor line would change what every existing
                 * row meant without anything on the page having said so.
                 */
                key: 'vendorName',
                header: 'Vendor',
                width: 150,
                render: (_: unknown, r: BomRow) => <VendorBadge row={r} />,
            },
            {
                key: 'materialType',
                header: 'Type',
                width: 70,
                render: (val: 'RM' | 'PM') => <MaterialTypeBadge type={val} />,
            },
            {
                key: 'materialSku',
                header: 'Material SKU',
                width: 'fit',
                render: (val: string | null) => (
                    <span style={{ fontSize: 13, color: val ? 'rgb(26, 32, 44)' : 'rgb(148, 163, 184)' }}>
                        {val ?? '--'}
                    </span>
                ),
            },
            {
                key: 'materialName',
                header: 'Material Name',
                width: 'fit',
                render: (val: string) => (
                    <span style={{ fontSize: 13, color: 'rgb(26, 32, 44)' }}>{val}</span>
                ),
            },
            {
                key: 'quantity',
                header: 'Quantity',
                // Wide enough for the edit row: an 80px box plus the save and cancel
                // buttons. At 130 the box shrank to ~22px of text and hid the number.
                width: 190,
                align: 'right' as const,
                render: (_: unknown, r: BomRow) => {
                    const key = makeKey(r);
                    const isEditing = editingKey === key;

                    if (isEditing) {
                        return (
                            <div className="flex items-center justify-end gap-1">
                                <input
                                    className="h-7 w-20 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                    value={draftValue}
                                    onChange={(e) => setDraftValue(e.target.value)}
                                    autoFocus
                                    disabled={saving}
                                />
                                <ActionIcon
                                    size="sm"
                                    radius="xl"
                                    variant="filled"
                                    color="dark"
                                    disabled={saving}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSave(r);
                                    }}
                                >
                                    <IconCheck size={14} />
                                </ActionIcon>
                                <ActionIcon
                                    size="sm"
                                    radius="xl"
                                    variant="subtle"
                                    color="gray"
                                    disabled={saving}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancel();
                                    }}
                                >
                                    <IconX size={14} />
                                </ActionIcon>
                            </div>
                        );
                    }

                    return (
                        <div className="flex items-center justify-end gap-2">
                            <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                {/* Six places, matching what is stored: the default of three
                                    showed 0.0065 as 0.007, which disagreed with the file. */}
                                {formatCount(r.quantity, { maxDecimals: 6 })}
                            </span>
                            <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="gray"
                                radius="xl"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingKey(key);
                                    setDraftValue(String(r.quantity));
                                }}
                            >
                                <IconPencil size={14} />
                            </ActionIcon>
                        </div>
                    );
                },
            },
            {
                key: 'unit',
                header: 'Unit',
                width: 80,
                render: (val: string) => (
                    <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                        {val ?? 'pcs'}
                    </span>
                ),
            },
            {
                key: 'actions',
                header: '',
                width: 60,
                align: 'right' as const,
                render: (_: unknown, r: BomRow) => (
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        radius="xl"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(r);
                        }}
                    >
                        <IconTrash size={14} />
                    </ActionIcon>
                ),
            },
        ],
        [editingKey, draftValue, saving],
    );

    return (
        <div className="space-y-4">
            <header className="space-y-1">
                <h2 className="text-xl font-semibold text-slate-900">
                    Bill of Materials
                </h2>
                <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-slate-500">
                        Download the BOM from the table below to get the template. Fill{' '}
                        <code className="rounded bg-slate-100 px-1">product_id</code> and{' '}
                        <code className="rounded bg-slate-100 px-1">material_id</code>, set{' '}
                        <code className="rounded bg-slate-100 px-1">quantity</code> per unit of product
                        in the material&apos;s own unit, and upload it back as CSV or Excel. Leave{' '}
                        <code className="rounded bg-slate-100 px-1">vendor_id</code> blank for a line
                        that applies to all vendors, or name one to give that vendor its own BOM for
                        the product — the column must be present either way. The other columns are
                        there to read.
                        Existing lines are updated and new ones inserted; a line left out of the file
                        is not deleted, use the row&apos;s delete icon for that. A unit is changed on
                        the material, not here.
                    </p>
                    <FileButton onChange={handleUpload} accept=".csv,.xlsx,.xls">
                        {(props) => (
                            <button
                                {...props}
                                type="button"
                                disabled={uploading}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    height: 32,
                                    padding: '0 12px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: 'rgb(15, 23, 42)',
                                    backgroundColor: 'white',
                                    border: '1px solid rgb(226, 232, 240)',
                                    borderRadius: 6,
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    opacity: uploading ? 0.6 : 1,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {uploading ? 'Uploading…' : 'Upload BOM'}
                            </button>
                        )}
                    </FileButton>
                </div>
            </header>

            {actionError && (
                <div className="rounded border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-700">{actionError}</p>
                </div>
            )}

            {uploadResult && (
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-medium text-slate-700">
                        {uploadResult.processed} row(s) processed
                        {uploadResult.errors.length > 0
                            ? `, ${uploadResult.errors.length} error(s)`
                            : ''}
                    </p>
                    {uploadResult.errors.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-red-600">
                            {uploadResult.errors.slice(0, 20).map((e) => (
                                <li key={`${e.row}-${e.productSku ?? ''}-${e.materialSku ?? ''}`}>
                                    Row {e.row}: {e.reason}
                                </li>
                            ))}
                            {uploadResult.errors.length > 20 && (
                                <li className="text-slate-500">
                                    ... and {uploadResult.errors.length - 20} more
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}

            {vendorPills.length > 2 && (
                <div className="flex flex-wrap items-center gap-2">
                    {vendorPills.map((pill) => {
                        const active = vendorFilter === pill.value;
                        return (
                            <button
                                key={pill.value || 'all'}
                                type="button"
                                onClick={() => setVendorFilter(pill.value)}
                                className="rounded-full px-3 py-1"
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    border: '1px solid',
                                    borderColor: active ? 'rgb(15, 23, 42)' : 'rgb(226, 232, 240)',
                                    backgroundColor: active ? 'rgb(15, 23, 42)' : 'white',
                                    color: active ? 'white' : 'rgb(71, 85, 105)',
                                    cursor: 'pointer',
                                }}
                            >
                                {pill.label}
                                <span style={{ opacity: 0.6, marginLeft: 6 }}>{pill.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <DataSheet
                data={filteredRows}
                columns={columns}
                isLoading={loading}
                getRowKey={makeKey}
                title="BOM Lines"
                subtitle={`${filteredRows.length} of ${rows.length} lines`}
                enableSearch
                searchPlaceholder="Search by product or material..."
                searchValue={search}
                onSearchChange={setSearch}
                enableDownload
                allColumns={BOM_EXPORT_COLUMNS}
                onFetchAllData={fetchAllForDownload}
                downloadFilename="bom.csv"
                toolbarControls={
                    <Group gap={8} wrap="nowrap">
                        <FilterDropdown
                            value={typeFilter}
                            options={BOM_TYPE_FILTER_OPTIONS}
                            onChange={setTypeFilter}
                            placeholder="All Types"
                        />
                        <Button
                            size="xs"
                            variant="filled"
                            color="dark"
                            leftSection={<IconPlus size={14} />}
                            onClick={openAddModal}
                        >
                            Add Line
                        </Button>
                    </Group>
                }
                emptyState={
                    <Text size="13px" c="dimmed" ta="center" p={32}>
                        No BOM mappings yet. Upload a CSV / Excel file or click Add Line to get started.
                    </Text>
                }
            />

            <Modal
                opened={addModalOpen}
                onClose={closeAddModal}
                title="Add BOM Line"
                size="md"
                centered
            >
                <Stack gap={12}>
                    <Select
                        label="Product"
                        placeholder={picksLoading ? 'Loading…' : 'Select a product'}
                        data={productPicks.map((p) => ({
                            value: p.productId,
                            label: p.sku ? `${p.name} (${p.sku})` : p.name || p.productId,
                        }))}
                        value={selectedProductId}
                        onChange={setSelectedProductId}
                        searchable
                        nothingFoundMessage="No products found"
                        disabled={picksLoading}
                    />
                    <Select
                        label="Vendor"
                        description="Leave this on All vendors unless one vendor makes this product differently."
                        data={vendorOptions}
                        value={selectedVendor}
                        onChange={(value) => setSelectedVendor(value ?? ALL_VENDORS)}
                        searchable
                        nothingFoundMessage="No vendors found"
                        disabled={picksLoading}
                    />
                    {canCopyShared && (
                        <div className="rounded border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs text-slate-600">
                                This product has {sharedLineCount} line
                                {sharedLineCount === 1 ? '' : 's'} that apply to all vendors, and
                                this vendor has none of its own yet. Copy them and edit what
                                differs — once a vendor has its own lines, those are the only ones
                                its production reads.
                            </p>
                            <Button
                                size="xs"
                                variant="default"
                                mt={8}
                                loading={copying}
                                onClick={handleCopyShared}
                            >
                                Copy {sharedLineCount} line{sharedLineCount === 1 ? '' : 's'}
                            </Button>
                        </div>
                    )}
                    <Select
                        label="Material"
                        placeholder={picksLoading ? 'Loading…' : 'Select a material'}
                        data={materialPicks.map((m) => ({
                            value: m.materialId,
                            label: m.sku
                                ? `${m.name} (${m.sku}) — ${m.materialType}`
                                : `${m.name} — ${m.materialType}`,
                        }))}
                        value={selectedMaterialId}
                        onChange={setSelectedMaterialId}
                        searchable
                        nothingFoundMessage="No materials found"
                        disabled={picksLoading}
                    />
                    <NumberInput
                        // The unit is the material's, so it can only be named once one is picked.
                        label={
                            selectedMaterialUnit
                                ? `Quantity per unit of product, in ${selectedMaterialUnit}`
                                : 'Quantity per unit of product'
                        }
                        placeholder="e.g. 0.0065"
                        value={newQuantity}
                        onChange={setNewQuantity}
                        // Six places, the same as the table stores. Two places refused every
                        // seasoning and pouch line a snack brand actually has.
                        min={0.000001}
                        decimalScale={6}
                    />
                    {actionError && (
                        <p className="text-xs text-red-600">{actionError}</p>
                    )}
                    <Group justify="flex-end" gap={8} mt={8}>
                        <Button variant="subtle" color="gray" onClick={closeAddModal} disabled={addSaving}>
                            Cancel
                        </Button>
                        <Button
                            color="dark"
                            onClick={handleAdd}
                            disabled={
                                !selectedProductId ||
                                !selectedMaterialId ||
                                !newQuantity ||
                                Number(newQuantity) <= 0
                            }
                            loading={addSaving}
                        >
                            Add Line
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
};

/* ───────────────────── Supply Vendor Tab (inline edit lead time) ───────────────────── */

const SupplyVendorListTab: React.FC = () => {
    const [vendors, setVendors] =
        useState<typeof SUPPLY_VENDORS>(SUPPLY_VENDORS);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [draftValue, setDraftValue] = useState('');

    const makeKey = (vendorName: string, row: VendorMaterialRow) =>
        `${vendorName}-${row.materialOrSku}`;

    const handleSave = (key: string) => {
        setVendors((prev) =>
            prev.map((vendor) => ({
                ...vendor,
                rows: vendor.rows.map((row) =>
                    makeKey(vendor.name, row) === key
                        ? { ...row, leadTimeDays: Number(draftValue) || 0 }
                        : row,
                ),
            })),
        );
        setEditingKey(null);
        setDraftValue('');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setDraftValue('');
    };

    const buildColumns = (
        vendorName: string,
    ): ColumnDef<VendorMaterialRow>[] => [
        { accessorKey: 'materialOrSku', header: 'Material/SKU' },
        {
            accessorKey: 'leadTimeDays',
            header: 'Lead Time (days)',
            cell: ({ row }) => {
                const original = row.original as VendorMaterialRow;
                const key = makeKey(vendorName, original);
                const isEditing = editingKey === key;

                if (isEditing) {
                    return (
                        <div className="flex items-center gap-1">
                            <input
                                className="h-7 w-16 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                value={draftValue}
                                onChange={(e) => setDraftValue(e.target.value)}
                                autoFocus
                            />
                            <ActionIcon
                                size="sm"
                                radius="xl"
                                variant="filled"
                                color="dark"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSave(key);
                                }}
                            >
                                <IconCheck size={14} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                radius="xl"
                                variant="subtle"
                                color="gray"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel();
                                }}
                            >
                                <IconX size={14} />
                            </ActionIcon>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center justify-between gap-2">
                        <span>{original.leadTimeDays}</span>
                        <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingKey(key);
                                setDraftValue(
                                    String(original.leadTimeDays ?? ''),
                                );
                            }}
                        >
                            <IconPencil size={14} />
                        </ActionIcon>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            <header>
                <h2 className="text-xl font-semibold text-slate-900">
                    Supply Vendor List
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                    Manage your supply vendors and their lead times
                </p>
            </header>

            {vendors.map((vendor) => (
                <section key={vendor.name} className="space-y-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-slate-900">
                                {vendor.name}
                            </p>
                            <span className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                {vendor.tag}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-0.5 text-xs text-slate-500">
                        <p>{vendor.billingAddress}</p>
                        <p>{vendor.dispatchAddress}</p>
                        <p>Tax Code: {vendor.taxCode}</p>
                    </div>

                    <DataTable<VendorMaterialRow>
                        data={vendor.rows}
                        columns={buildColumns(vendor.name)}
                        enableGlobalSearch={false}
                        enablePagination={false}
                        classes={tableClasses}
                    />
                </section>
            ))}
        </div>
    );
};

/* ───────────────────── Sales Channel Tab (view only) ───────────────────── */

const SalesChannelSkuListingsTab: React.FC = () => {
    const columns: ColumnDef<SalesChannelRow>[] = [
        { accessorKey: 'internalSku', header: 'Internal SKU' },
        { accessorKey: 'productName', header: 'Product Name' },
        { accessorKey: 'amazon', header: 'Amazon' },
        { accessorKey: 'flipkart', header: 'Flipkart' },
        { accessorKey: 'nykaa', header: 'Nykaa' },
        { accessorKey: 'ownWebsite', header: 'Own Website' },
    ];

    return (
        <div className="space-y-4">
            <header>
                <h2 className="text-xl font-semibold text-slate-900">
                    Sales Channel SKU Listings
                </h2>
            </header>

            <DataTable<SalesChannelRow>
                data={SALES_CHANNEL_ROWS}
                columns={columns}
                enableGlobalSearch={false}
                enablePagination={false}
                classes={tableClasses}
            />
        </div>
    );
};

/* ───────────────────── Warehouses Tab (inline edit min stock) ───────────────────── */

const PrimaryWarehousesTab: React.FC = () => {
    const [rows, setRows] = useState<WarehouseRow[]>(WAREHOUSE_ROWS);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [draftValue, setDraftValue] = useState('');

    const makeKey = (row: WarehouseRow) => row.warehouseName;

    const handleSave = (key: string) => {
        setRows((prev) =>
            prev.map((row) =>
                makeKey(row) === key
                    ? { ...row, minStockLevel: draftValue }
                    : row,
            ),
        );
        setEditingKey(null);
        setDraftValue('');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setDraftValue('');
    };

    const columns: ColumnDef<WarehouseRow>[] = [
        { accessorKey: 'warehouseName', header: 'Warehouse Name' },
        { accessorKey: 'location', header: 'Location' },
        {
            accessorKey: 'minStockLevel',
            header: 'Min Stock Level (units)',
            cell: ({ row }) => {
                const original = row.original as WarehouseRow;
                const key = makeKey(original);
                const isEditing = editingKey === key;

                if (isEditing) {
                    return (
                        <div className="flex items-center gap-1">
                            <input
                                className="h-7 w-20 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                value={draftValue}
                                onChange={(e) => setDraftValue(e.target.value)}
                                autoFocus
                            />
                            <ActionIcon
                                size="sm"
                                radius="xl"
                                variant="filled"
                                color="dark"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSave(key);
                                }}
                            >
                                <IconCheck size={14} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                radius="xl"
                                variant="subtle"
                                color="gray"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel();
                                }}
                            >
                                <IconX size={14} />
                            </ActionIcon>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center justify-between gap-2">
                        <span>{original.minStockLevel}</span>
                        <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingKey(key);
                                setDraftValue(original.minStockLevel);
                            }}
                        >
                            <IconPencil size={14} />
                        </ActionIcon>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-4">
            <header>
                <h2 className="text-xl font-semibold text-slate-900">
                    Primary Warehouses
                </h2>
            </header>

            <DataTable<WarehouseRow>
                data={rows}
                columns={columns}
                enableGlobalSearch={false}
                enablePagination={false}
                classes={tableClasses}
            />
        </div>
    );
};

/* ───────────────────── Product Classification Tab ───────────────────── */

type TierRow = { label: string; cumulativePercentage: number };

type ScoringMode = 'revenue' | 'units' | 'weighted_blend';

const DEFAULT_TIERS: TierRow[] = [
    { label: 'A', cumulativePercentage: 80 },
    { label: 'B', cumulativePercentage: 95 },
    { label: 'C', cumulativePercentage: 100 },
];

function deriveScoringState(config: any): { mode: ScoringMode; revenueWeight: number } {
    const expr = config?.scoringExpression;
    if (!expr || (expr.type !== 'weighted_sum' && expr.type !== 'normalized_weighted_sum')) {
        return { mode: 'revenue', revenueWeight: 100 };
    }

    const revTerm = expr.terms?.find((t: any) => t.metric === 'revenue');
    const unitsTerm = expr.terms?.find((t: any) => t.metric === 'units');

    if (revTerm && !unitsTerm) return { mode: 'revenue', revenueWeight: 100 };
    if (!revTerm && unitsTerm) return { mode: 'units', revenueWeight: 0 };
    if (revTerm && unitsTerm) return { mode: 'weighted_blend', revenueWeight: Math.round(revTerm.weight * 100) };

    return { mode: 'revenue', revenueWeight: 100 };
}

function buildScoringExpression(mode: ScoringMode, revenueWeight: number) {
    if (mode === 'revenue') return { type: 'weighted_sum', terms: [{ metric: 'revenue', weight: 1 }] };
    if (mode === 'units') return { type: 'weighted_sum', terms: [{ metric: 'units', weight: 1 }] };
    return {
        type: 'normalized_weighted_sum',
        terms: [
            { metric: 'revenue', weight: revenueWeight / 100 },
            { metric: 'units', weight: (100 - revenueWeight) / 100 },
        ],
    };
}

const SCORING_MODE_LABELS: Record<ScoringMode, string> = {
    revenue: 'Revenue',
    units: 'Units Sold',
    weighted_blend: 'Weighted Blend',
};

/* ───────────────────── Warehouse Ops Settings Tab ───────────────────── */

const WarehouseOpsSettingsTab: React.FC = () => {
    const queryClient = useQueryClient();
    const userJson = (() => {
        try { return JSON.parse(localStorage.getItem('user') ?? '{}'); } catch { return {}; }
    })();
    const merchantId: string | null = userJson.organizationId ?? null;

    const orgQuery = useQuery({
        queryKey: ['org-config', merchantId],
        queryFn: () => getOrgConfig(merchantId!),
        enabled: !!merchantId,
    });

    const toggleMutation = useMutation({
        mutationFn: (enabled: boolean) => setWarehouseOpsEnabled(merchantId!, enabled),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-config', merchantId] }),
    });

    const enabled = orgQuery.data?.warehouseOpsEnabled ?? false;

    return (
        <Stack gap="lg">
            <div>
                <Text fw={600} size="sm" mb={4}>Warehouse Operations</Text>
                <Text size="xs" c="dimmed" mb="md">
                    Enable Warehouse Ops to allow your team to create and manage GRNs, STNs, QC checks, and putaway from within the platform.
                    When disabled, the Warehouse Ops section is hidden from all users in this organisation.
                </Text>
                <Group gap="sm" align="center">
                    <Text size="sm">{enabled ? 'Enabled' : 'Disabled'}</Text>
                    <Button
                        size="xs"
                        variant={enabled ? 'outline' : 'filled'}
                        color={enabled ? 'red' : 'green'}
                        loading={toggleMutation.isPending}
                        onClick={() => toggleMutation.mutate(!enabled)}
                    >
                        {enabled ? 'Disable Warehouse Ops' : 'Enable Warehouse Ops'}
                    </Button>
                </Group>
            </div>
        </Stack>
    );
};

/* ───────────────────── Inventory Views Tab ───────────────────── */

/**
 * Brand-level opening state for the Inventory grids. A user who changes the grouping
 * dropdown still overrides this for themselves on their own browser; this only decides
 * what someone sees before they have ever touched it.
 */
/** "30, 60, 90, 180" -> [30, 60, 90, 180]; null when it is not ascending positive whole days. */
const parseAgeingEdges = (text: string): number[] | null => {
    const parts = text.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length === 0 || parts.length > 8) return null;
    const edges: number[] = [];
    for (const part of parts) {
        if (!/^\d+$/.test(part)) return null;
        const value = Number(part);
        if (value < 1 || value > 3650) return null;
        if (edges.length > 0 && value <= edges[edges.length - 1]) return null;
        edges.push(value);
    }
    return edges;
};

const InventoryViewsSettingsTab: React.FC = () => {
    const queryClient = useQueryClient();
    const userJson = (() => {
        try { return JSON.parse(localStorage.getItem('user') ?? '{}'); } catch { return {}; }
    })();
    const merchantId: string | null = userJson.organizationId ?? null;

    const orgQuery = useQuery({
        queryKey: ['org-config', merchantId],
        queryFn: () => getOrgConfig(merchantId!),
        enabled: !!merchantId,
    });

    const saveMutation = useMutation({
        mutationFn: (rollupBy: string) =>
            setInventoryViewDefaults(
                merchantId!,
                buildInventoryViewDefaults(
                    orgQuery.data?.inventoryViewDefaults,
                    'drr',
                    rollupBy,
                ),
            ),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-config', merchantId] }),
    });

    const savedRollup =
        orgQuery.data?.inventoryViewDefaults?.drr?.rollupBy ?? NO_ROLLUP;

    /**
     * Only the first grouping level is settable. The second is a per-visit refinement, and
     * offering it here would need this control to enforce GroupingLevels' own rules (no
     * second level without a first, no repeat of the first) for little gain.
     */
    const healthMutation = useMutation({
        mutationFn: (groupBy: string) =>
            setInventoryViewDefaults(
                merchantId!,
                buildReplenishmentHealthDefaults(
                    orgQuery.data?.inventoryViewDefaults,
                    groupBy,
                    NO_GROUPING,
                ),
            ),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-config', merchantId] }),
    });

    const savedHealthGrouping =
        orgQuery.data?.inventoryViewDefaults?.replenishmentHealth?.groupBy ??
        NO_GROUPING;

    const stockoutMutation = useMutation({
        mutationFn: (enabled: boolean) => setDrrExcludeStockoutDays(merchantId!, enabled),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-config', merchantId] }),
    });

    const excludeStockoutDays = orgQuery.data?.drrExcludeStockoutDays ?? false;

    const savedAgeingEdges =
        orgQuery.data?.inventoryViewDefaults?.ageing?.bucketEdges ?? DEFAULT_AGEING_EDGES;
    const [ageingEdgesDraft, setAgeingEdgesDraft] = useState<string | null>(null);
    const ageingEdgesText = ageingEdgesDraft ?? savedAgeingEdges.join(', ');
    const parsedAgeingEdges = parseAgeingEdges(ageingEdgesText);
    const ageingEdgesDirty =
        parsedAgeingEdges !== null &&
        parsedAgeingEdges.join(',') !== savedAgeingEdges.join(',');

    const ageingMutation = useMutation({
        mutationFn: (edges: number[]) =>
            setInventoryViewDefaults(
                merchantId!,
                buildInventoryAgeingDefaults(
                    orgQuery.data?.inventoryViewDefaults,
                    edges.join(',') === DEFAULT_AGEING_EDGES.join(',') ? undefined : edges,
                ),
            ),
        onSuccess: () => {
            setAgeingEdgesDraft(null);
            void queryClient.invalidateQueries({ queryKey: ['org-config', merchantId] });
        },
    });

    return (
        <Stack gap="lg">
            <div>
                <Text fw={600} size="sm" mb={4}>DoH View — default row grouping</Text>
                <Text size="xs" c="dimmed" mb="md">
                    How the Inventory &rarr; DoH View table opens for everyone in this organisation.
                    Anyone can still change the grouping from the toolbar; their choice is remembered
                    for them and does not change this default.
                </Text>
                <Group gap="sm" align="center">
                    <Select
                        size="xs"
                        w={220}
                        data={STATIC_ROLLUP_OPTIONS}
                        value={savedRollup}
                        disabled={orgQuery.isLoading || saveMutation.isPending}
                        allowDeselect={false}
                        onChange={(val) => {
                            if (val && val !== savedRollup) saveMutation.mutate(val);
                        }}
                    />
                    {saveMutation.isPending && (
                        <Text size="xs" c="dimmed">Saving…</Text>
                    )}
                    {saveMutation.isError && (
                        <Text size="xs" c="red">Could not save. Try again.</Text>
                    )}
                </Group>
            </div>

            <div>
                <Text fw={600} size="sm" mb={4}>Inventory health — default row grouping</Text>
                <Text size="xs" c="dimmed" mb="md">
                    How the Replenishment &rarr; Inventory health table opens for everyone in
                    this organisation. Anyone can still change the grouping from the toolbar
                    for their visit; it returns to this on the next load.
                </Text>
                <Group gap="sm" align="center">
                    <Select
                        size="xs"
                        w={220}
                        data={PRODUCT_GROUPINGS}
                        value={savedHealthGrouping}
                        disabled={orgQuery.isLoading || healthMutation.isPending}
                        allowDeselect={false}
                        onChange={(val) => {
                            if (val && val !== savedHealthGrouping) {
                                healthMutation.mutate(val);
                            }
                        }}
                    />
                    {healthMutation.isPending && (
                        <Text size="xs" c="dimmed">Saving…</Text>
                    )}
                    {healthMutation.isError && (
                        <Text size="xs" c="red">Could not save. Try again.</Text>
                    )}
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                    The page opens on Finished goods, so material-only groupings are not
                    offered here. The second nesting level stays a per-visit choice.
                </Text>
            </div>

            <div>
                <Text fw={600} size="sm" mb={4}>SKU Ageing — age buckets</Text>
                <Text size="xs" c="dimmed" mb="md">
                    The day boundaries the Inventory &rarr; SKU Ageing table buckets stock into,
                    counted from the day each unit was received. Ascending whole days, comma
                    separated; the last bucket is open-ended. The default is 30, 60, 90, 180.
                </Text>
                <Group gap="sm" align="center">
                    <TextInput
                        size="xs"
                        w={220}
                        value={ageingEdgesText}
                        placeholder="30, 60, 90, 180"
                        error={parsedAgeingEdges === null ? 'Ascending whole days, e.g. 30, 60, 90, 180' : undefined}
                        disabled={orgQuery.isLoading || ageingMutation.isPending}
                        onChange={(e) => setAgeingEdgesDraft(e.currentTarget.value)}
                    />
                    <Button
                        size="xs"
                        variant="filled"
                        loading={ageingMutation.isPending}
                        disabled={!ageingEdgesDirty}
                        onClick={() => {
                            if (parsedAgeingEdges) ageingMutation.mutate(parsedAgeingEdges);
                        }}
                    >
                        Save
                    </Button>
                    {ageingMutation.isError && (
                        <Text size="xs" c="red">Could not save. Try again.</Text>
                    )}
                </Group>
            </div>

            <div>
                <Text fw={600} size="sm" mb={4}>DRR — exclude out-of-stock days</Text>
                <Text size="xs" c="dimmed" mb="md">
                    A run rate is units sold divided by the days the SKU could actually have
                    sold. With this on, days when a SKU had nothing to sell are dropped from
                    that divisor, so a product that sold 60 units in the 20 days it was in
                    stock reads 3/day rather than 2/day over 30.
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                    A day only counts as lost when <b>every</b> warehouse that could have
                    served it was out — the channel&apos;s own fulfilment centres for SOR
                    channels, the internal warehouses for OR and direct-to-consumer ones. A
                    partial outage is still a normal selling day.
                </Text>
                <Group gap="sm" align="center">
                    <Text size="sm">{excludeStockoutDays ? 'On' : 'Off'}</Text>
                    <Button
                        size="xs"
                        variant={excludeStockoutDays ? 'outline' : 'filled'}
                        color={excludeStockoutDays ? 'red' : 'green'}
                        loading={stockoutMutation.isPending}
                        disabled={orgQuery.isLoading}
                        onClick={() => stockoutMutation.mutate(!excludeStockoutDays)}
                    >
                        {excludeStockoutDays ? 'Turn off' : 'Turn on'}
                    </Button>
                    {stockoutMutation.isError && (
                        <Text size="xs" c="red">Could not save. Try again.</Text>
                    )}
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                    This changes every DRR, days-of-holding, reorder point and plan quantity
                    across the platform at once. Turn it on only once this brand&apos;s
                    stockout feed has been checked.
                </Text>
            </div>
        </Stack>
    );
};

/* ───────────────────── Shelf Locations Tab ───────────────────── */

const ShelfLocationsTab: React.FC = () => {
    const warehousesQuery = useQuery<Array<{ warehouseId: string; name: string }>>({
        queryKey: ['warehouses-picker-internal'],
        queryFn: () => listWarehousesForPicker(),
    });

    const warehouses = warehousesQuery.data ?? [];

    if (warehousesQuery.isLoading) {
        return <Text size="sm" c="dimmed">Loading warehouses...</Text>;
    }

    if (warehouses.length === 0) {
        return <Text size="sm" c="dimmed">No internal warehouses found.</Text>;
    }

    return (
        <Stack gap="xl">
            {warehouses.map((wh) => (
                <ShelfLocationManager
                    key={wh.warehouseId}
                    warehouseId={wh.warehouseId}
                    warehouseName={wh.name}
                />
            ))}
        </Stack>
    );
};

const ProductClassificationTab: React.FC = () => {
    const [lookbackPeriod, setLookbackPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'half-yearly'>('monthly');
    const [recalculationFrequency, setRecalculationFrequency] = useState<'weekly' | 'monthly' | 'quarterly' | 'half-yearly'>('monthly');
    const [tiers, setTiers] = useState<TierRow[]>(DEFAULT_TIERS);
    const [scoringMode, setScoringMode] = useState<ScoringMode>('revenue');
    const [revenueWeight, setRevenueWeight] = useState(70);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [draftLabel, setDraftLabel] = useState('');
    const [draftPercentage, setDraftPercentage] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);
    const [configLoaded, setConfigLoaded] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            const merchantId = await getMerchantId();
            if (!merchantId) return;

            const org = await ReparoApi<any>({
                method: 'GET',
                url: `/organizations/${merchantId}`,
                body: undefined,
            });

            if (org?.productTierConfig) {
                const config = org.productTierConfig;
                setLookbackPeriod(config.lookbackPeriod || 'monthly');
                setRecalculationFrequency(config.recalculationFrequency || 'monthly');
                if (config.tiers?.length) {
                    setTiers(config.tiers);
                }
                const { mode, revenueWeight: rw } = deriveScoringState(config);
                setScoringMode(mode);
                setRevenueWeight(rw);
            }
            setConfigLoaded(true);
        } catch (err) {
            console.error('Failed to load tier config', err);
            setConfigLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (!configLoaded) {
            loadConfig();
        }
    }, [configLoaded, loadConfig]);

    const handleSaveConfig = async () => {
        setSaving(true);
        setSaveMessage(null);
        try {
            const merchantId = await getMerchantId();
            if (!merchantId) throw new Error('Merchant ID not found');

            await ReparoApi({
                method: 'PATCH',
                url: `/organizations/${merchantId}`,
                body: JSON.stringify({
                    productTierConfig: {
                        lookbackPeriod,
                        recalculationFrequency,
                        tiers,
                        scoringExpression: buildScoringExpression(scoringMode, revenueWeight),
                    },
                }),
            });

            await ReparoApi({
                method: 'POST',
                url: `/jobs/tier-recalculation/trigger/${merchantId}`,
            });

            setSaveMessage('Config saved and tier assignments recalculated.');
        } catch (err: any) {
            setSaveMessage(err?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleEditSave = (key: string) => {
        const pct = parseFloat(draftPercentage);
        if (!draftLabel.trim() || isNaN(pct) || pct < 0 || pct > 100) return;
        setTiers((prev) =>
            prev.map((t) =>
                t.label === key
                    ? { label: draftLabel.trim(), cumulativePercentage: pct }
                    : t,
            ),
        );
        setEditingKey(null);
    };

    const handleEditCancel = () => {
        setEditingKey(null);
        setDraftLabel('');
        setDraftPercentage('');
    };

    const addTier = () => {
        const nextLabel = String.fromCharCode(65 + tiers.length); // A, B, C, D...
        setTiers((prev) => [...prev, { label: nextLabel, cumulativePercentage: 100 }]);
    };

    const removeTier = (label: string) => {
        setTiers((prev) => prev.filter((t) => t.label !== label));
    };

    const tierColumns: ColumnDef<TierRow>[] = [
        {
            accessorKey: 'label',
            header: () => <span className="flex w-full justify-start">Tier Label</span>,
            cell: ({ row }) => {
                const original = row.original as TierRow;
                const isEditing = editingKey === original.label;

                if (isEditing) {
                    return (
                        <input
                            className="h-7 w-16 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                            value={draftLabel}
                            onChange={(e) => setDraftLabel(e.target.value)}
                            autoFocus
                        />
                    );
                }
                return <span className="font-medium">{original.label}</span>;
            },
        },
        {
            accessorKey: 'cumulativePercentage',
            header: () => (
                <span className="flex w-full justify-start">
                    {scoringMode === 'revenue' ? 'Cumulative Revenue %' : scoringMode === 'units' ? 'Cumulative Units %' : 'Cumulative Score %'}
                </span>
            ),
            cell: ({ row }) => {
                const original = row.original as TierRow;
                const isEditing = editingKey === original.label;

                if (isEditing) {
                    return (
                        <div className="flex items-center gap-1">
                            <input
                                className="h-7 w-20 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                value={draftPercentage}
                                onChange={(e) => setDraftPercentage(e.target.value)}
                                type="number"
                                min={0}
                                max={100}
                            />
                            <ActionIcon size="sm" radius="xl" variant="filled" color="dark" onClick={() => handleEditSave(original.label)}>
                                <IconCheck size={14} />
                            </ActionIcon>
                            <ActionIcon size="sm" radius="xl" variant="subtle" color="gray" onClick={handleEditCancel}>
                                <IconX size={14} />
                            </ActionIcon>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center justify-between gap-2">
                        <span>{original.cumulativePercentage}%</span>
                        <div className="flex items-center gap-1">
                            <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="gray"
                                radius="xl"
                                onClick={() => {
                                    setEditingKey(original.label);
                                    setDraftLabel(original.label);
                                    setDraftPercentage(String(original.cumulativePercentage));
                                }}
                            >
                                <IconPencil size={14} />
                            </ActionIcon>
                            <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="red"
                                radius="xl"
                                onClick={() => removeTier(original.label)}
                            >
                                <IconTrash size={14} />
                            </ActionIcon>
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-8">
            {/* Section A: Tier Config */}
            <section className="space-y-4">
                <header className="flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-slate-900">
                            Product Tier Config
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">
                            Configure ABC classification tiers based on cumulative revenue contribution
                        </p>
                    </div>
                    {/* What each tier MEANS for ordering is set where it is acted on:
                        target cover on the replenishment plan view, reorder rules on
                        inventory health. Only the definition lives here. */}
                </header>

                <div className="flex gap-8">
                    <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-slate-600 whitespace-nowrap">
                            Lookback Period
                        </label>
                        <select
                            className="h-8 w-40 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                            value={lookbackPeriod}
                            onChange={(e) => setLookbackPeriod(e.target.value as 'weekly' | 'monthly' | 'quarterly' | 'half-yearly')}
                        >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="half-yearly">Half-Yearly</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-slate-600 whitespace-nowrap">
                            Recalculation Frequency
                        </label>
                        <select
                            className="h-8 w-40 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                            value={recalculationFrequency}
                            onChange={(e) => setRecalculationFrequency(e.target.value as 'weekly' | 'monthly' | 'quarterly' | 'half-yearly')}
                        >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="half-yearly">Half-Yearly</option>
                        </select>
                    </div>
                </div>

                {/* Scoring Method */}
                <div className="space-y-3">
                    <div>
                        <p className="text-xs font-medium text-slate-600">Scoring Method</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                            How products are scored before ranking into tiers
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {(['revenue', 'units', 'weighted_blend'] as ScoringMode[]).map((mode) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setScoringMode(mode)}
                                className={cn(
                                    'cursor-pointer rounded-xl border px-3 py-1.5 text-xs font-medium transition-all',
                                    scoringMode === mode
                                        ? 'border-slate-900 bg-slate-900 text-white'
                                        : 'border-slate-200 text-slate-600 hover:border-slate-400',
                                )}
                            >
                                {SCORING_MODE_LABELS[mode]}
                            </button>
                        ))}
                    </div>

                    {scoringMode === 'weighted_blend' && (
                        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <span className="w-20 text-xs text-slate-600">Revenue</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={revenueWeight}
                                    onChange={(e) => setRevenueWeight(Math.min(100, Math.max(0, Number(e.target.value))))}
                                    className="h-7 w-20 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                />
                                <span className="text-xs text-slate-400">%</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="w-20 text-xs text-slate-600">Units Sold</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={100 - revenueWeight}
                                    onChange={(e) => setRevenueWeight(Math.min(100, Math.max(0, 100 - Number(e.target.value))))}
                                    className="h-7 w-20 rounded-md border border-slate-300 px-2 text-xs focus:ring-1 focus:ring-slate-400 focus:outline-none"
                                />
                                <span className="text-xs text-slate-400">%</span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Formula:{' '}
                                <span className="font-mono text-slate-600">
                                    {revenueWeight > 0 && `${(revenueWeight / 100).toFixed(2)} × revenue`}
                                    {revenueWeight > 0 && revenueWeight < 100 && ' + '}
                                    {revenueWeight < 100 && `${((100 - revenueWeight) / 100).toFixed(2)} × units`}
                                </span>
                            </p>
                        </div>
                    )}
                </div>

                <DataTable<TierRow>
                    data={tiers}
                    columns={tierColumns}
                    enableGlobalSearch={false}
                    enablePagination={false}
                    classes={tableClasses}
                />

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={addTier}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                    >
                        <IconPlus size={14} />
                        Add Tier
                    </button>

                    <button
                        type="button"
                        onClick={handleSaveConfig}
                        disabled={saving || tiers.length === 0}
                        className="rounded-md bg-slate-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Config'}
                    </button>
                </div>

                {saveMessage && (
                    <p className={`text-xs ${saveMessage.includes('Failed') ? 'text-red-500' : 'text-green-600'}`}>
                        {saveMessage}
                    </p>
                )}
            </section>

        </div>
    );
};

/* ───────────────────── Main Modal ───────────────────── */

export const SettingsModal: React.FC<SettingsModalProps> = ({
    opened,
    onClose,
}) => {
    // `/settings?section=b2bCustomers` (e.g. a customer's "Edit in Settings" link) opens that section.
    const [params] = useSearchParams();
    const { currentUser } = useCurrentUser();
    const { isLocked } = useLockedModules();
    const sections = SETTINGS_SECTIONS.filter((section) => !section.adminOnly || currentUser?.role === 'ADMIN');
    const openSections = sections.filter((section) => !isLocked(section.module));
    const premiumSections = sections.filter((section) => isLocked(section.module));
    const requested = params.get('section') as SettingSectionKey | null;
    const [activeSection, setActiveSection] = useState<SettingSectionKey>(
        requested && SETTINGS_SECTIONS.some((section) => section.key === requested) ? requested : 'distributionPaths',
    );
    const shownSection = sections.some((section) => section.key === activeSection) ? activeSection : 'distributionPaths';
    const shownIsPremium = premiumSections.some((section) => section.key === shownSection);
    const shownLabel = SETTINGS_SECTIONS.find((section) => section.key === shownSection)?.label ?? '';
    // Folded by default; a deep link straight to a locked section opens the list so the
    // highlighted entry is not hidden behind its own header.
    const [premiumOpen, setPremiumOpen] = useState(false);
    const premiumListOpen = premiumOpen || shownIsPremium;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            withCloseButton
            centered
            padding={0}
            size="90vw"
            radius="lg"
            overlayProps={{
                blur: 10,
                backgroundOpacity: 0.78,
                className: 'settings-shine-overlay',
            }}
            classNames={{
                content:
                    'rounded-3xl overflow-hidden w-full shadow-xl',
                body: 'p-0',
                header: 'px-6 pt-5 pb-3 border-b border-slate-200',
                title: 'text-base font-semibold text-slate-900',
                close: 'text-slate-500 hover:bg-slate-100',
            }}
            title="Settings"
        >
            <div className="flex h-[85vh] flex-col bg-slate-50 md:flex-row">
                {/* Left tabs – smooth grey pill style */}
                <aside className="w-full border-b border-slate-200 bg-slate-50 px-4 py-4 md:w-64 md:border-r md:border-b-0">
                    <Text fw={600} size="sm" className="mb-4 text-slate-700">
                        Settings
                    </Text>

                    <nav className="flex flex-row gap-2 overflow-x-auto md:flex-col md:gap-1">
                        {openSections.map((section) => {
                            const active = shownSection === section.key;
                            return (
                                <button
                                    key={section.key}
                                    type="button"
                                    onClick={() =>
                                        setActiveSection(section.key)
                                    }
                                    className={cn(
                                        'flex cursor-pointer items-center rounded-2xl border border-transparent px-3 py-2 text-left text-sm font-medium whitespace-nowrap transition-all',
                                        active
                                            ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                                            : 'text-slate-600 hover:border-slate-200 hover:bg-slate-100 hover:text-slate-900',
                                    )}
                                >
                                    {section.label}
                                </button>
                            );
                        })}
                        {premiumSections.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setPremiumOpen((open) => !open)}
                                    aria-expanded={premiumListOpen}
                                    className="mt-3 flex cursor-pointer items-center gap-1 rounded-2xl px-3 py-1.5 text-left text-[10px] font-semibold tracking-[0.12em] text-slate-400 uppercase transition-all hover:bg-slate-100 hover:text-slate-600"
                                >
                                    <Lock size={10} weight="fill" />
                                    {PREMIUM_GROUP_LABEL} · {premiumSections.length}
                                    {premiumListOpen ? (
                                        <CaretDown size={10} weight="bold" className="ml-auto" />
                                    ) : (
                                        <CaretRight size={10} weight="bold" className="ml-auto" />
                                    )}
                                </button>
                                {premiumListOpen && premiumSections.map((section) => {
                                    const active = shownSection === section.key;
                                    return (
                                        <button
                                            key={section.key}
                                            type="button"
                                            title={PREMIUM_TOOLTIP}
                                            onClick={() => setActiveSection(section.key)}
                                            className={cn(
                                                'flex cursor-pointer items-center justify-between rounded-2xl border border-transparent px-3 py-2 text-left text-sm font-medium whitespace-nowrap transition-all',
                                                active
                                                    ? 'border-slate-300 bg-slate-200 text-slate-600'
                                                    : 'text-slate-400 hover:border-slate-200 hover:bg-slate-100',
                                            )}
                                        >
                                            {section.label}
                                            <Lock size={12} weight="fill" className="ml-2 shrink-0" />
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </nav>
                </aside>

                {/* Right content */}
                <main className="flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
                    {shownIsPremium && <PremiumSectionNotice label={shownLabel} />}
                    {!shownIsPremium && shownSection === 'brandLetterhead' && <BrandLetterheadTab />}
                    {!shownIsPremium && shownSection === 'documentNumbering' && <DocumentNumberingTab />}
                    {!shownIsPremium && shownSection === 'productClassification' && (
                        <ProductClassificationTab />
                    )}
                    {!shownIsPremium && shownSection === 'entityMaster' && (
                        <EntityMasterSection />
                    )}
                    {!shownIsPremium && shownSection === 'vendorDetails' && <VendorSection />}
                    {!shownIsPremium && shownSection === 'emailSettings' && <EmailSettingsTab />}
                    {!shownIsPremium && shownSection === 'lanes' && <LanesTab />}
                    {!shownIsPremium && shownSection === 'distributionPaths' && (
                        <DistributionPathsTab />
                    )}
                    {!shownIsPremium && shownSection === 'distributionEmail' && (
                        <DistributionEmailTab />
                    )}
                    {!shownIsPremium && shownSection === 'billOfMaterials' && (
                        <BillOfMaterialsTab />
                    )}
                    {!shownIsPremium && shownSection === 'shelfLocations' && (
                        <ShelfLocationsTab />
                    )}
                    {!shownIsPremium && shownSection === 'warehouseOps' && (
                        <WarehouseOpsSettingsTab />
                    )}
                    {!shownIsPremium && shownSection === 'b2bCustomers' && <B2bSettingsTab />}
                    {!shownIsPremium && shownSection === 'inventoryViews' && (
                        <InventoryViewsSettingsTab />
                    )}
                    {!shownIsPremium && shownSection === 'users' && <UsersTab />}
                </main>
            </div>
        </Modal>
    );
};
